import { isEmpty, round, split } from 'lodash';
import { evaluate, parse } from 'mathjs';
import { INVENTORY_TYPES } from 'src/constants';
import { toDecimal4 } from 'src/utils/toDecimal';

export const getParticularsAmount = particulars => {
  if (particulars.length === 0) return 0;

  return particulars
    .map(({ Amt }) => Amt.value)
    .reduce((prev, curr, indx) => Number(prev) + Number(curr));
};

export const validateForm = ({ data, excludes = [], allowZeroFlds = [] }) => {
  let newData = { ...data };
  let hasError = false;

  Object.entries(newData).forEach(([key, { require, value, ...rest }]) => {
    const isRequired = require || rest?.isRequired || rest?.required;

    if (
      !isRequired ||
      excludes.includes(key) ||
      (allowZeroFlds.includes(key) && value === 0)
    ) {
      return;
    }

    if (rest?.type === 'json' && rest?.ui_component === 'sub_acc') {
      const id = value?.id ?? '';
      const empty = id === '' || id === 0;

      if (empty) {
        newData[key].error = true;
        newData[key].errorMessage = 'This field is required';
        hasError = true;
        return;
      }
    }

    const empty = value === 0 || value === '' || typeof value === 'undefined';
    if (empty) {
      newData[key].error = true;
      newData[key].errorMessage = 'This field is required';
      hasError = true;
      return;
    }
  });

  return { hasError, newData };
};

export const solveExpression = ({ expression = '', variables = {} }) => {
  const exp = [...expression];

  if (exp[0] !== '=') return 'Invalid formula';

  exp.splice(0, 1); // remove '='

  try {
    return evaluate(exp.join(''), variables);
  } catch (error) {
    return 'Invalid formula';
  }
};

export const safeSolveExpression = ({
  expression = '',
  variables = {},
  defaults = 0
} = {}) => {
  // 1. Return early if expression isn't a valid formula string
  if (typeof expression !== 'string' || !expression.startsWith('=')) {
    return defaults;
  }

  // 2. Clone variables and sanitize existing values
  const safeVars = {};
  if (variables && typeof variables === 'object') {
    Object.keys(variables).forEach(key => {
      const val = variables[key];

      if (typeof val === 'number' && !isNaN(val)) {
        safeVars[key] = val;
      } else {
        const isNumStr =
          val !== null &&
          val !== undefined &&
          val !== '' &&
          !isNaN(Number(val));
        safeVars[key] = isNumStr ? Number(val) : 0;
      }
    });
  }

  // 3. Find missing variables in the expression and set them to 0
  const cleanExpr = expression.slice(1).trim();
  try {
    const node = parse(cleanExpr);
    const symbols = node.filter(n => n.isSymbolNode);

    symbols.forEach(symbol => {
      if (!(symbol.name in safeVars)) {
        safeVars[symbol.name] = 0;
      }
    });
  } catch (err) {
    // If parsing fails, solveExpression handles it
  }

  // 4. Delegate execution to solveExpression
  const rawResult = solveExpression({ expression, variables: safeVars });

  // 5. Unbox mathjs BigNumber / ResultSet to a standard JS Number
  const numericResult = Number(
    rawResult?.valueOf ? rawResult.valueOf() : rawResult
  );

  // Return numeric result if valid, else return defaults
  return !isNaN(numericResult) && isFinite(numericResult)
    ? numericResult
    : defaults ?? 0;
};

export const setSelfValueVariable = ({ value, formula = '' }) => {
  const selfVariables = ['cos'];
  let copyFormula = formula;

  for (let i = 0; i < selfVariables.length; i++) {
    const item = selfVariables[i];

    if (copyFormula.indexOf(item))
      copyFormula = copyFormula.replace(item, value);
  }

  return copyFormula;
};

export const recomputeJournalEntry = ({ journalEntries, variables }) => {
  let copyJournalEntries = [...journalEntries];

  let updatedJERows = [];

  if (copyJournalEntries.length === 0) return [];

  for (let i = 0; i < journalEntries.length; i++) {
    const item = journalEntries[i];

    // if no formula just get the bal
    if (item.Cr.formula === '' && item.Dr.formula === '') continue;

    const amountType = item.Cr.formula && item.Cr.formula !== '' ? 'Cr' : 'Dr';

    const expression = setSelfValueVariable({
      value: item[amountType].value,
      formula: item[amountType].formula
    });

    const taxVars = item.tax ?? {};
    const taxVariables = {
      taxVatIN: taxVars.vatIN || 0,
      taxVatOUT: taxVars.vatOUT || 0,
      taxGross: taxVars.gross || 0,
      taxWtRate: taxVars.wtRate || 0,
      taxWtIN: taxVars.wtAmtIN || 0,
      taxWtOUT: taxVars.wtAmtOUT || 0,
      taxVatWTrate: taxVars.vatWTrate || 0,
      taxVatWTamt: taxVars.vatWTamt || 0,
      taxReqVAT: taxVars.reqVAT || 0,
      taxReqWT: taxVars.reqWT || 0,
      taxReqVATDeferred: taxVars.reqVATDeferred || 0,
      taxTaxNonvat: taxVars.tax_nonvat || 0,
      taxTaxZerorated: taxVars.tax_zerorated || 0,
      taxTaxVatable: taxVars.tax_vatable || 0
    };

    const newVariables = {
      ...variables,
      ...taxVariables
    };
    // If NaN value return 0
    const result = isNaN(
      solveExpression({
        expression,
        variables: newVariables
      })
    )
      ? 0
      : solveExpression({ expression, variables: newVariables });
    if (result === 'Invalid formula') {
      copyJournalEntries[i][amountType].value = 0; // update the row
      copyJournalEntries[i][amountType].error = true; // update the row
      copyJournalEntries[i][amountType].errorMessage =
        'Invalid Formula : ' + item[amountType].formula; // update the row

      continue;
    }

    // if expression result is the same with stored amount just update the balance
    if (round(result, 4) === round(item[amountType].value, 4)) {
      copyJournalEntries[i][amountType].value = result; // update the row
      continue;
    }

    copyJournalEntries[i][amountType].value = result; // update the row

    // create a copy of updated row
    let updatedRow = { ...item };
    updatedRow[amountType].value = result;

    // push updated row
    updatedJERows.push({
      updatedRow,
      jeIndex: i
    });
  }

  return updatedJERows;
};

export const getKVSTableVariables = (value, kvs_key, settings) => {
  //Table Type get total for accounts
  //FORMAT: jv_kvs_<key>_<field>
  const obj = settings.columns.reduce((acc, { field, total }) => {
    if (total) {
      acc = {
        ...acc,
        [`jv_kvs_${kvs_key}_${field}`]: value.reduce(
          (t, p) => t + (p?.[field] ?? 0),
          0
        )
      };
    }
    return acc;
  }, {});

  const otherVars = value.reduce((acc, prev) => {
    if (Boolean(prev?.createVariable)) {
      acc = {
        ...acc,
        [`jv_kvs_${kvs_key}_var_${prev?.createVariable?.name}`]:
          prev?.[prev?.createVariable?.key] ?? 0
      };
    }
    return acc;
  }, {});

  return { ...obj, ...otherVars };
};

export const getKvsNumberValues = kvs => {
  let kvsNumberValues = {};

  Object.entries(kvs).forEach(([key, { type, value, ...rest }]) => {
    if (rest?.ui_component === 'select' && type !== 'int') return;

    if (rest?.ui_component === 'sc_pwd_discount') {
      kvsNumberValues = {
        ...kvsNumberValues,
        [`jv_kvs_${key}_amount`]: value?.amount ?? 0
      };
    }

    if (rest?.ui_component === 'table') {
      const kvsTableVariables = getKVSTableVariables(value, key, rest);
      kvsNumberValues = {
        ...kvsNumberValues,
        ...kvsTableVariables
      };
    }

    if (type === 'int') {
      kvsNumberValues = {
        ...kvsNumberValues,
        [`jv_kvs_${key}`]: isNaN(value) ? 0 : Number(value)
      };
    }
  });
  return kvsNumberValues;
};

const getValueFromSubAccount = (key, subAccountDetails) => {
  const splittedKey = split(key, '.');

  if (splittedKey.length === 1)
    return subAccountDetails?.[splittedKey[0]] ?? '';

  if (splittedKey[0] === 'kvs') {
    const kvs = subAccountDetails?.kvs ?? [];
    const keyIndex = kvs.findIndex(({ key }) => key === splittedKey[1]);

    return kvs?.[keyIndex]?.value ?? '';
  }

  return '';
};

export const saveSubAccountDetailsToKVS = ({
  settings = {},
  jvKVS = {},
  subAccountDetails = {}
}) => {
  let toUpdate = {};

  //! No settings
  if (isEmpty(settings)) return toUpdate;

  // * map_values => update kvs value where the source is sub account
  if (settings?.map_values) {
    Object.entries(settings?.map_values).forEach(([key, value]) => {
      //! key not found
      if (!Boolean(jvKVS[key])) return;

      toUpdate = {
        ...toUpdate,
        [key]: getValueFromSubAccount(value, subAccountDetails)
      };
    });
  }

  // * onchange_update_jv_kvs_values
  if (settings?.onchange_update_jv_kvs_values) {
    let updatedKvs = {};

    //* apply new updates
    Object.entries(jvKVS).forEach(([key, { value }]) => {
      updatedKvs = {
        ...updatedKvs,
        [key]: toUpdate?.[key] ?? value
      };
    });

    //* key => kvs key to update
    //* props.key => source key
    //* source_map

    Object.entries(settings?.onchange_update_jv_kvs_values).forEach(
      ([key, props]) => {
        //* if key to update not exist
        if (!updatedKvs.hasOwnProperty(key)) return;

        //* if source key not exist
        if (!props?.key) return;

        //* source map not exist
        if (!props?.source_map) return;

        // * create source
        let source = {};
        Object.entries(props.source_map).map(([key_source, value]) => {
          source = {
            ...source,
            [key_source]: updatedKvs?.[value] ?? ''
          };
        });

        //* get the key value
        const keyValue = updatedKvs?.[props.key] ?? '';

        toUpdate = {
          ...toUpdate,
          [key]: source?.[keyValue] ?? ''
        };
      }
    );
  }

  return toUpdate;
};

export const getInventoryVariables = ({
  inventory = [],
  invType = 0,
  amtType = 'OUT'
}) => {
  const {
    invGrossAmt,
    invPriceIN,
    invPriceOUT,
    invCurrentPrice,
    invTotalMU,
    invTotalDiscOUT,

    //PPD and NDP related
    invTotalDiscQtyRate,
    invTotalDiscQtyAmt,

    invTotalDisc,
    invTotalDiscValue,
    invTotalGrossAmt,
    invTotalGrossOfVAT,
    invTotalNetOfVAT,

    invTotalNonVAT,
    invTotalVatable,
    invTotalExempt,
    invTotalZeroRated,

    invNewPrice,
    invNewVatIN,
    invNewPriceTotalNonVAT,
    invNewPriceTotalVatable,
    invNewPriceTotalExempt,
    invNewPriceTotalZeroRated,
    invRebate,

    invAmt,
    invVatIN,
    invVatOUT,
    invTotalItems,
    invTotalQtyIN,
    invTotalQtyOUT,
    invTotalAmtIN,
    invTotalAmtOUT
  } = inventory.reduce(
    (acc, prev) => {
      const qty = toDecimal4(+prev.qty.value),
        price = +prev.price.value,
        muAmt = toDecimal4(+prev.muAmt),
        //PPD and NDP related
        discQtyRate = toDecimal4(+prev.discQtyRate),
        discQtyAmt = toDecimal4(+prev.discQtyAmt),
        //--------
        discOutrightAmt = toDecimal4(+prev.discOutrightAmt),
        discAmt = toDecimal4(+prev.discAmt),
        discValue = toDecimal4(+prev.discValue),
        vatOUTAmt = toDecimal4(+prev.vatOUTAmt),
        vatINAmt = toDecimal4(+prev.vatINAmt),
        new_vatINAmt = toDecimal4(+prev.new_vatINAmt),
        priceINAmt = toDecimal4(+prev.priceIN) * qty,
        priceOUTAmt = toDecimal4(+prev.priceOUT) * qty,
        currentPriceAmt = toDecimal4(+prev?.currentPrice?.value),
        newPriceAmt = toDecimal4(+prev.new_price.value) * qty,
        grossAmt = toDecimal4(qty * price),
        taxablePrice = toDecimal4(+prev.taxablePrice),
        totalTaxableAmt = toDecimal4(+prev.taxableAmt || taxablePrice * qty),
        vatCode = prev.vatCode,
        qtyREC = prev.qtyREC,
        qtyACT = prev?.qtyACT?.value ?? 0,
        rebate = prev?.rebate ?? 0;
      let vatAmt = 0;

      if (prev.isDeleted) return acc;

      acc.invGrossAmt += grossAmt;
      acc.invTotalMU += muAmt * qty;

      //PPD and NDP related
      acc.invTotalDiscQtyRate += discQtyRate;
      acc.invTotalDiscQtyAmt += discQtyAmt;
      //--------

      acc.invTotalDiscOUT += discOutrightAmt;
      acc.invTotalDisc += discAmt;
      acc.invTotalDiscValue += discValue;
      acc.invTotalGrossAmt += grossAmt;
      acc.invAmt +=
        // remove times qty cousing inbalance total
        // grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
        grossAmt + muAmt * qty - discOutrightAmt - discAmt - discValue;

      acc.invTotalItems += qty;

      acc.invVatIN += vatINAmt;
      acc.invVatOUT += vatOUTAmt;

      acc.invPriceIN += priceINAmt;
      acc.invPriceOUT += priceOUTAmt;
      acc.invCurrentPrice += currentPriceAmt;
      if (prev.qtyType === 'IN') {
        if (invType === INVENTORY_TYPES.STOCK_ADJUSTMENT_BY_VARIANCE) {
          acc.invTotalQtyIN += qtyACT - qtyREC;
        } else acc.invTotalQtyIN += qty;

        acc.invTotalAmtIN +=
          grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
      }

      if (prev.qtyType === 'OUT') {
        if (invType === INVENTORY_TYPES.STOCK_ADJUSTMENT_BY_VARIANCE) {
          acc.invTotalQtyOUT += qtyREC - qtyACT;
        } else acc.invTotalQtyOUT += qty;

        acc.invTotalAmtOUT +=
          // remove times qty cousing inbalance total
          // grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
          grossAmt + muAmt * qty - discOutrightAmt - discAmt - discValue;
      }

      acc.invNewPrice += newPriceAmt;
      acc.invNewVatIN += new_vatINAmt;
      acc.invRebate += rebate;

      if (amtType === 'OUT') vatAmt = vatOUTAmt;

      if (amtType === 'IN') vatAmt = vatINAmt;

      acc.invTotalGrossOfVAT += totalTaxableAmt;
      acc.invTotalNetOfVAT += totalTaxableAmt - vatAmt;

      if (vatCode === 0) acc.invTotalNonVAT += totalTaxableAmt;
      else if (vatCode === 1) acc.invTotalVatable += totalTaxableAmt - vatAmt;
      else if (vatCode === 2) acc.invTotalExempt += totalTaxableAmt;
      else if (vatCode === 3) acc.invTotalZeroRated += totalTaxableAmt;

      if (vatCode === 0) acc.invNewPriceTotalNonVAT += newPriceAmt;
      else if (vatCode === 1)
        acc.invNewPriceTotalVatable += newPriceAmt - new_vatINAmt;
      else if (vatCode === 2) acc.invNewPriceTotalExempt += newPriceAmt;
      else if (vatCode === 3) acc.invNewPriceTotalZeroRated += newPriceAmt;

      return acc;
    },
    {
      invGrossAmt: 0,
      invPriceIN: 0,
      invPriceOUT: 0,
      invCurrentPrice: 0,
      invTotalMU: 0,
      invTotalDiscOUT: 0,
      invTotalDiscQtyRate: 0,
      invTotalDiscQtyAmt: 0,
      invTotalDisc: 0,
      invTotalDiscValue: 0,
      invTotalGrossAmt: 0,
      invTotalGrossOfVAT: 0,
      invTotalNetOfVAT: 0,

      invTotalNonVAT: 0,
      invTotalVatable: 0,
      invTotalExempt: 0,
      invTotalZeroRated: 0,

      invNewPrice: 0,
      invNewVatIN: 0,
      invNewPriceTotalNonVAT: 0,
      invNewPriceTotalVatable: 0,
      invNewPriceTotalExempt: 0,
      invNewPriceTotalZeroRated: 0,
      invRebate: 0,

      invAmt: 0,
      invVatIN: 0,
      invVatOUT: 0,
      invTotalItems: 0,
      invTotalQtyIN: 0,
      invTotalQtyOUT: 0,
      invTotalAmtIN: 0,
      invTotalAmtOUT: 0
    }
  );

  return {
    invGrossAmt: invGrossAmt,
    invPriceIN: invPriceIN,
    invPriceOUT: invPriceOUT,
    invCurrentPrice: invCurrentPrice,
    invTotalMU: invTotalMU,
    invTotalDiscQtyRate: invTotalDiscQtyRate,
    invTotalDiscQtyAmt: invTotalDiscQtyAmt,
    invTotalDiscOUT: toDecimal4(invTotalDiscOUT),
    invTotalDisc: invTotalDisc,
    invTotalDiscValue: invTotalDiscValue,
    invTotalGrossAmt: invTotalGrossAmt,
    invTotalGrossOfVAT: invTotalGrossOfVAT,
    invTotalNetOfVAT: invTotalNetOfVAT,

    invTotalNonVAT: invTotalNonVAT,
    invTotalVatable: toDecimal4(invTotalVatable),
    invTotalExempt: invTotalExempt,
    invTotalZeroRated: invTotalZeroRated,

    invNewPrice: invNewPrice,
    invNewVatIN: invNewVatIN,
    invNewPriceTotalNonVAT: invNewPriceTotalNonVAT,
    invNewPriceTotalVatable: invNewPriceTotalVatable,
    invNewPriceTotalExempt: invNewPriceTotalExempt,
    invNewPriceTotalZeroRated: invNewPriceTotalZeroRated,
    invRebate: invRebate,

    invTotalAmtIN: toDecimal4(invTotalAmtIN),
    invTotalAmtOUT: toDecimal4(invTotalAmtOUT),

    invAmt: invAmt,
    invVatIN: invVatIN,
    invVatOUT: invVatOUT,
    invTotalItems,
    invTotalQtyIN,
    invTotalQtyOUT
  };
};

export const getJournalEntryVariables = ({
  journalEntries = [],
  cos_acc_id = 0,
  mi_account = 0,
  isAbsolute = true
}) => {
  const {
    bal,
    totalDr,
    totalCr,
    jeVatIN,
    jeVatOUT,
    jeWtIN,
    jeWtOUT,
    jeVatWtAmt,
    jeCOS,
    jeMI,
    jeQtyAndRateTotal,
    jeTaxGross,
    accountAmounts
  } = journalEntries.reduce(
    (acc, prev) => {
      const Dr = parseFloat(+prev.Dr.value);
      const Cr = parseFloat(+prev.Cr.value);
      const diff = Dr - Cr;

      const vatIN = parseFloat(+prev?.tax?.vatIN ?? 0);
      const vatOUT = parseFloat(+prev?.tax?.vatOUT ?? 0);
      const wtAmtIN = parseFloat(+prev?.tax?.wtAmtIN ?? 0);
      const wtAmtOUT = parseFloat(+prev?.tax?.wtAmtOUT ?? 0);
      const vatWTamt = parseFloat(+prev?.tax?.vatWTamt ?? 0);

      const reqQtyRate = +prev.reqQtyRate;
      const drQty = +prev.drQty;
      const crQty = +prev.crQty;
      const drRate = parseFloat(+prev.drRate);
      const crRate = parseFloat(+prev.crRate);
      const taxGross = parseFloat(+prev?.tax?.gross ?? 0);
      const ixAcc = +prev.ixAcc.value;

      acc.totalDr += Dr;
      acc.totalCr += Cr;
      acc.bal += diff;

      acc.jeVatIN += vatIN;
      acc.jeVatOUT += vatOUT;
      acc.jeWtIN += wtAmtIN;
      acc.jeWtOUT += wtAmtOUT;
      acc.jeVatWtAmt += vatWTamt;
      acc.jeTaxGross += taxGross;

      if (+prev.ixAcc.value === +cos_acc_id) acc.jeCOS += Dr;

      if (+prev.ixAcc.value === +mi_account) acc.jeMI += Cr;

      if (reqQtyRate === 1) acc.jeQtyAndRateTotal += drRate * drQty;

      if (reqQtyRate === 2) acc.jeQtyAndRateTotal += crRate * crQty;

      acc.accountAmounts[`acc_${ixAcc}_dr`] = Dr;
      acc.accountAmounts[`acc_${ixAcc}_cr`] = Cr;
      return acc;
    },
    {
      bal: 0,
      totalDr: 0,
      totalCr: 0,
      jeVatIN: 0,
      jeVatOUT: 0,
      jeWtIN: 0,
      jeWtOUT: 0,
      jeVatWtAmt: 0,
      jeCOS: 0,
      jeMI: 0,
      jeQtyAndRateTotal: 0,
      jeTaxGross: 0,
      accountAmounts: {}
    }
  );

  return {
    bal: isAbsolute ? Math.abs(bal) : bal,
    totalDr: totalDr,
    totalCr: totalCr,
    jeVatIN: jeVatIN,
    jeVatOUT: jeVatOUT,
    jeWtIN: jeWtIN,
    jeWtOUT: jeWtOUT,
    jeVatWtAmt: jeVatWtAmt,
    jeCOS: jeCOS,
    jeMI: jeMI,
    jeQtyAndRateTotal: jeQtyAndRateTotal,
    jeTaxGross: jeTaxGross,
    ...accountAmounts
  };
};

export function fillSubAccounts(
  ref,
  subDetails,
  refKey = '',
  allowReset = false
) {
  if (refKey === '') return;

  for (let i = 1; i <= 5; i++) {
    const currentRef = ref[`${refKey}${i}`];

    if (!Boolean(currentRef)) {
      continue;
    }

    for (let j = 0; j <= 5; j++) {
      let ixSubLinkType = 0,
        ixSubLink = 0,
        sSubLink = '';

      if (j === 0) {
        ixSubLinkType = subDetails?.['ixSubParentType'] ?? 0;
        ixSubLink = subDetails?.['ixSubParent'] ?? 0;
        sSubLink = subDetails?.['sSubParent'] ?? '';
      } else {
        ixSubLinkType = subDetails?.['ixSubLinkType' + j] ?? 0;
        ixSubLink = subDetails?.['ixSubLink' + j] ?? 0;
        sSubLink = subDetails?.['sSubLink' + j] ?? '';
      }

      const isSlotEmpty = currentRef.sub_id === 0 || currentRef.sub_id === '';
      const canPopulate = isSlotEmpty || allowReset;
      if (
        ixSubLinkType !== 0 &&
        ixSubLinkType === currentRef.ixSubType &&
        canPopulate &&
        currentRef.sub_id !== ixSubLink
      ) {
        currentRef.sub_id = ixSubLink;
        currentRef.value = ixSubLink;
        currentRef.sub_title = sSubLink;
        currentRef.error = false;
        currentRef.errorMessage = '';
      }
    }
  }
}
